import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';

const MultiSelectDropdown = ({
  label,
  placeholder = 'Select options',
  options = [],
  selectedValues = [],
  onChange,
  getOptionLabel = (opt) => opt?.name || String(opt?.id || ''),
  getOptionValue = (opt) => opt?.id,
  showSelectAll = false,
  selectAllLabel = 'Select All',
  disabled = false,
  searchPlaceholder = 'Search...',
}) => {
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const containerRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  useEffect(() => {
    if (!open) {
      setSearchText('');
    }
  }, [open]);

  const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);
  const filteredOptions = useMemo(() => {
    const term = searchText.trim().toLowerCase();
    if (!term) return options;
    return options.filter((opt) => String(getOptionLabel(opt) || '').toLowerCase().includes(term));
  }, [options, searchText, getOptionLabel]);

  const selectedText = useMemo(() => {
    if (!selectedValues.length) return placeholder;
    const selectedLabels = options
      .filter((opt) => selectedSet.has(getOptionValue(opt)))
      .map((opt) => getOptionLabel(opt));
    return selectedLabels.length > 2
      ? `${selectedLabels.slice(0, 2).join(', ')} +${selectedLabels.length - 2}`
      : selectedLabels.join(', ');
  }, [selectedValues, options, selectedSet, getOptionLabel, getOptionValue, placeholder]);

  const onToggle = (value) => {
    const next = selectedSet.has(value)
      ? selectedValues.filter((v) => v !== value)
      : [...selectedValues, value];
    onChange(next);
  };

  const onToggleAll = () => {
    const allValues = options.map((opt) => getOptionValue(opt));
    const allSelected = allValues.length > 0 && allValues.every((value) => selectedSet.has(value));
    onChange(allSelected ? [] : allValues);
  };

  const allSelected = options.length > 0 && options.every((opt) => selectedSet.has(getOptionValue(opt)));

  return (
    <div className="flex flex-col relative" ref={containerRef}>
      <label className="text-xs text-gray-600 mb-1">{label}</label>
      <button
        type="button"
        className="border rounded p-2 text-sm w-full flex items-center justify-between disabled:bg-gray-100 disabled:text-gray-500"
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
      >
        <span className="truncate text-left">{selectedText}</span>
        <ChevronDownIcon className="h-4 w-4 text-gray-500 ml-2" />
      </button>

      {open && !disabled && (
        <div className="absolute top-full left-0 z-20 mt-1 w-full max-h-60 overflow-auto border rounded bg-white shadow">
          <div className="sticky top-0 bg-white p-2 border-b">
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full border rounded px-2 py-1 text-sm"
            />
          </div>
          {showSelectAll && options.length > 0 && (
            <label className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 text-sm cursor-pointer border-b">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={onToggleAll}
              />
              <span>{selectAllLabel}</span>
            </label>
          )}
          {filteredOptions.length === 0 ? (
            <p className="text-sm text-gray-500 p-2">
              {options.length === 0 ? 'No options available' : 'No matching options'}
            </p>
          ) : (
            filteredOptions.map((option) => {
              const value = getOptionValue(option);
              return (
                <label key={value} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedSet.has(value)}
                    onChange={() => onToggle(value)}
                  />
                  <span>{getOptionLabel(option)}</span>
                </label>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
